import { useEffect, useRef } from 'react';

import { LoadingScreen } from '@/components/loaders/LoadingScreen';
import { createFinalizeNavigate } from '@/features/auth/shared/utils/createFinalizeNavigate';
import { redirectToLogin } from '@/features/auth/shared/utils/redirectToLogin';
import { useClerk, useSignIn, useSignUp } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, type NavigateFunction } from 'react-router';

const handleSignInComplete = async (signIn: ReturnType<typeof useSignIn>['signIn'], navigate: NavigateFunction) => {
  if (signIn.status === 'complete' && signIn.createdSessionId) {
    await signIn.finalize({ navigate: createFinalizeNavigate(navigate) });
    return true;
  }
  return false;
};

interface SsoMessages {
  unknown: string;
  additionalVerification: string;
  accountNotFound: string;
}

const handleSsoCallback = async (
  clerk: ReturnType<typeof useClerk>,
  signIn: ReturnType<typeof useSignIn>['signIn'],
  signUp: ReturnType<typeof useSignUp>['signUp'],
  navigate: NavigateFunction,
  messages: SsoMessages
) => {
  // 1. Sign-in complete → activate session
  if (await handleSignInComplete(signIn, navigate)) return;

  // 2. Sign-up used existing account → transfer to sign-in
  if (signUp.isTransferable) {
    const { error } = await signIn.create({ transfer: true });
    if (error) {
      console.error('SSO transfer to sign-in error:', error);
      redirectToLogin(messages.unknown);
      return;
    }
    if (await handleSignInComplete(signIn, navigate)) return;
    redirectToLogin(messages.unknown);
    return;
  }

  // 3. Needs first factor (e.g. password) — redirect unless only enterprise_sso factors
  if (
    signIn.status === 'needs_first_factor' &&
    !signIn.supportedFirstFactors.every((f) => f.strategy === 'enterprise_sso')
  ) {
    redirectToLogin(messages.additionalVerification);
    return;
  }

  // 4. Sign-in transferable → external account not found, transfer to sign-up
  if (signIn.isTransferable) {
    const { error } = await signUp.create({ transfer: true });
    if (error) {
      console.error('SSO transfer to sign-up error:', error);
      redirectToLogin(messages.accountNotFound);
      return;
    }
    if (signUp.status === 'complete' && signUp.createdSessionId) {
      await signUp.finalize({ navigate: createFinalizeNavigate(navigate) });
      return;
    }
    redirectToLogin(messages.accountNotFound);
    return;
  }

  // 5. Sign-up complete (new user created)
  if (signUp.status === 'complete' && signUp.createdSessionId) {
    await signUp.finalize({ navigate: createFinalizeNavigate(navigate) });
    return;
  }

  // 6. Needs second factor or new password
  if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_new_password') {
    redirectToLogin(messages.additionalVerification);
    return;
  }

  // 7. Existing session — reactivate (not finalize, since it's not a fresh sign-in/up)
  const existingSessionId = signIn.existingSession?.sessionId ?? signUp.existingSession?.sessionId;
  if (existingSessionId !== undefined) {
    await clerk.setActive({ session: existingSessionId, navigate: createFinalizeNavigate(navigate) });
    return;
  }

  // 8. Fallback — unhandled Clerk state; most likely the email is not registered.
  // Genuine JS/network errors are caught by the .catch() at the call site (unknown message).
  console.error('SSO callback: unhandled state', { signInStatus: signIn.status, signUpStatus: signUp.status });
  redirectToLogin(messages.accountNotFound);
};

export const SsoCallbackScreen = () => {
  const clerk = useClerk();
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasRun = useRef(false);

  useEffect(() => {
    if (!clerk.loaded || hasRun.current) return;
    hasRun.current = true;

    const messages: SsoMessages = {
      unknown: t('error.login.unknown'),
      additionalVerification: t('error.login.additionalVerificationRequired'),
      accountNotFound: t('error.login.accountNotFound')
    };

    handleSsoCallback(clerk, signIn, signUp, navigate, messages).catch((err: unknown) => {
      console.error('SSO callback error:', err);
      redirectToLogin(messages.unknown);
    });
  }, [clerk, signIn, signUp, navigate, t]);

  return <LoadingScreen message="loading.session" />;
};
